import { GameState, GameStateEnum } from "../fondue";
import { borne, closestPointOnEllipse } from "./ellipse-utils";


export default class PicAFondue {
    readonly gameState: GameState;
    readonly spriteImg = new Image();

    readonly breadImg = new Image();

    readonly spriteWidth = 185;
    readonly spriteHeight = 259;
    
    readonly centerX = 365;
    readonly centerY = 195;

    readonly potWidth = 110;
    readonly potHeight = 45;

    readonly initialPos = {x: this.centerX + 160, y: this.centerY - 50, r: 0.2 * Math.PI};

    currentFrame = 1;
    baseSpeed = 100;
    pos = {...this.initialPos};
    targetPos = {...this.initialPos};
    speed = {x: 0, y: 0, r: 0};
    targetReached = true;
    onTargetReached = () => {};

    lostPiece = false;

    constructor(gameState: GameState) {
        this.gameState = gameState;
        this.spriteImg.src = '/pic_a_fondue.png';
        this.breadImg.src = '/bout_de_pain.png'
    }

    get cheeseQuantity(): number {
        return this.gameState.cheeseQuantity || 0;
    }

    set cheeseQuantity(newValue: number) {
        this.gameState.cheeseQuantity = newValue;
    }

    startMoving() {
        this.baseSpeed = 30;
        this.targetPos.x = this.centerX;
        this.targetPos.y = this.centerY + (100 - this.gameState.cheeseLevel);
        this.targetPos.r = 0;
        this.lostPiece = false;
        this.targetReached = false;
        this.onTargetReached = () => {
            this.baseSpeed = 100;
            this.gameState.gameState = GameStateEnum.insidePot;
            this.onTargetReached = () => {};
        };
    }

    retrieve(keepGoing = true, setFinalScores?: (scores: number []) => void) {
        this.gameState.gameState = GameStateEnum.retrieving;
        this.baseSpeed = 30;
        this.targetPos = {...this.initialPos};
        this.targetReached = false;

        this.onTargetReached = () => {
            this.gameState.gameState = GameStateEnum.idle;
            if (!this.lostPiece && this.cheeseQuantity !== 0) {
                this.gameState.scores.push(this.cheeseQuantity);
            }
            this.currentFrame = 1;
            this.cheeseQuantity = 0;

            if (! keepGoing) {
                this.gameState.gameState = GameStateEnum.scores;
                setFinalScores?.(this.gameState.scores);
            }

            this.onTargetReached = () => {};
        };
    }

    getOffset(ctx: CanvasRenderingContext2D, zoom: number) {
        return {
            x: (ctx.canvas.getBoundingClientRect().width - window.innerWidth) / 2 / zoom,
            y: (ctx.canvas.getBoundingClientRect().height - window.innerHeight) / 2 / zoom
        }
    }

    setTargetFromMouse(ctx: CanvasRenderingContext2D, zoom: number) {
        if (this.gameState.mousePos.x === 0 && this.gameState.mousePos.y === 0) return;

        const offset = this.getOffset(ctx, zoom)

        let mouseGameX = this.gameState.mousePos.x / zoom + offset.x;
        let mouseGameY = this.gameState.mousePos.y / zoom + offset.y;

        // Find the target point (mouse if inside ellipse, or closest point on ellipse if outside)
        const targetPoint = closestPointOnEllipse(
            mouseGameX,
            mouseGameY,
            this.centerX,
            this.centerY + (100 - this.gameState.cheeseLevel),
            this.potWidth - (100 - this.gameState.cheeseLevel) / 2,
            this.potHeight
        );

        this.targetPos.x = targetPoint.x;
        this.targetPos.y = targetPoint.y;
        this.targetPos.r = 0;
    }

    moveInsideThePot(deltaTime: number, ctx: CanvasRenderingContext2D, zoom: number) {
        this.setTargetFromMouse(ctx, zoom);

        const speedFactor = 1 / (Math.pow(this.cheeseQuantity/1000, 0.7) + 1);

        this.moveTowardsTarget(deltaTime, speedFactor);
        
        const totalSpeed = Math.sqrt(Math.pow(this.speed.x, 2) + Math.pow(this.speed.y, 2));

        if (!this.lostPiece) {
            this.cheeseQuantity += totalSpeed * deltaTime / 5000;
            this.currentFrame = 1 + this.getFrameFromScore(this.cheeseQuantity);
            this.checkIfPieceIsLost(deltaTime, totalSpeed);
        }
    }

    getFrameFromScore(score: number) {
        return Math.min(Math.floor(score / 1000), 4)
    }

    getScoreFromCheese(cheese: number): number {
        let score = 0;
        let remaining = cheese;
        let multiplier = 1;
        
        while (remaining > 0) {
            const chunk = Math.min(remaining, 1000);
            score += multiplier * chunk;
            remaining -= chunk;
            multiplier++;
        }
        
        return score / 5;
    }

    checkIfPieceIsLost(deltaTime: number, totalSpeed: number) {
        const probaOfLostPiece = Math.pow(this.cheeseQuantity * totalSpeed / 100000, 3) * deltaTime / 100000;

        if (Math.random() * 10000 < probaOfLostPiece) {
            this.currentFrame = 0;
            this.lostPiece = true;
            this.cheeseQuantity = 0;
        }

    }

    moveTowardsTarget(deltaTime: number, speedFactor: number) {
        // Move towards the target point
        this.speed.x = this.baseSpeed * borne(-100, this.targetPos.x - this.pos.x, 100) * speedFactor;
        this.speed.y = this.baseSpeed * borne(-100, this.targetPos.y - this.pos.y, 100) * speedFactor;
        this.speed.r = this.baseSpeed * (this.targetPos.r - this.pos.r) * speedFactor;
        
        this.pos.x = this.pos.x + this.speed.x * deltaTime / 10000;
        this.pos.y = this.pos.y + this.speed.y * deltaTime / 10000;
        this.pos.r = this.pos.r + this.speed.r * deltaTime / 10000;

        const sumSpeed = Math.abs(this.speed.x) + Math.abs(this.speed.y) + Math.abs(this.speed.r);
        
        if (!this.targetReached && sumSpeed < 100) {
            this.targetReached = true;
            this.onTargetReached();
        }

        if (this.targetReached && sumSpeed > 100) {
            this.targetReached = false;
        }
    }

    draw(deltaTime: number, ctx: CanvasRenderingContext2D, zoom: number) {
        if (!this.spriteImg.complete) return;

        if (this.gameState.gameState === GameStateEnum.insidePot) {
            this.moveInsideThePot(deltaTime, ctx, zoom);
        } else {
            this.moveTowardsTarget(deltaTime, 1);
        }

        const posX = zoom * (this.pos.x - 70);
        const posY = zoom * (this.pos.y - 170);

        const centerX = posX + (zoom * 135);
        const centerY = posY + (zoom * 60);

        ctx.translate(centerX, centerY);
        ctx.rotate(this.pos.r);
        ctx.translate(-centerX, -centerY);
        ctx.drawImage(
            this.spriteImg,
            0, this.currentFrame * this.spriteHeight + 1, // source x, y
            this.spriteWidth, this.spriteHeight, // source width, height
            posX, posY, // destination x, y
            zoom * this.spriteWidth, zoom * this.spriteHeight // destination width, height
        );
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    drawScores(ctx: CanvasRenderingContext2D, zoom: number, scores: number[]) {
        const offset = this.getOffset(ctx, zoom);

        scores.forEach((s, index) => {
            ctx.drawImage(
                this.breadImg,
                0, this.getFrameFromScore(s) * 50, // source x, y
                this.breadImg.width, 50, // source width, height
                zoom * (690 - offset.x - 50 * scores.length + 50 * index), zoom * (340 - offset.y), // destination x, y
                zoom * this.breadImg.width, zoom * 50 // destination width, height
            );
        });
    }
}